import { pdf } from "@react-pdf/renderer";
import {
  CityReportPdfDocument,
  type GetReportPdfData,
} from "./CityReportPdfDocument";

// Target width for photo embeds. 800px at print resolution is sharp without
// bloating file size (a typical phone photo at 4032px would otherwise add
// ~3MB per image to the PDF).
const PHOTO_TARGET_WIDTH = 800;
const PHOTO_JPEG_QUALITY = 0.7;

/**
 * Fetch an image URL, draw it into an off-screen canvas at the target width,
 * and return a JPEG data URL. Returns null on any failure so the PDF can
 * still render without that photo rather than aborting the whole export.
 */
async function fetchAndDownscale(url: string): Promise<string | null> {
  try {
    const blob = await fetch(url, { mode: "cors" }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    });
    const bitmap = await createImageBitmap(blob);
    const ratio = bitmap.height / bitmap.width;
    const targetWidth = Math.min(PHOTO_TARGET_WIDTH, bitmap.width);
    const targetHeight = Math.round(targetWidth * ratio);

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close?.();
    return canvas.toDataURL("image/jpeg", PHOTO_JPEG_QUALITY);
  } catch {
    return null;
  }
}

/**
 * Render the city report PDF entirely in the browser:
 * 1. Prefetch + downscale every station photo + the client logo to data URLs.
 * 2. Render the React-PDF document with bytes already embedded.
 * 3. Return a Blob the caller can trigger a download from.
 */
export async function buildCityReportPdf(
  data: Omit<GetReportPdfData, "generatedAt">,
): Promise<Blob> {
  const photoEntries = data.stations.flatMap((s) =>
    s.photos.map((p) => [p.id, p.url] as const),
  );

  const [photoResults, logoDataUrl] = await Promise.all([
    Promise.all(
      photoEntries.map(async ([id, url]) => {
        const dataUrl = await fetchAndDownscale(url);
        return [id, dataUrl] as const;
      }),
    ),
    data.collection.logo_url
      ? fetchAndDownscale(data.collection.logo_url)
      : Promise.resolve(null),
  ]);

  const photoDataUrls: Record<string, string> = {};
  for (const [id, dataUrl] of photoResults) {
    if (dataUrl) photoDataUrls[id] = dataUrl;
  }

  const doc = CityReportPdfDocument({
    data: { ...data, generatedAt: new Date() },
    photoDataUrls,
    clientLogoDataUrl: logoDataUrl,
  });

  return await pdf(doc).toBlob();
}
